import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const HasilPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCompetitionName, setSelectedCompetitionName] = useState('');
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/lapangbola/competitions`);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setCompetitions(response.data.data);
        const firstComp = response.data.data[0];
        setSelectedCompetition(firstComp.id);
        setSelectedCompetitionName(firstComp.name);
        fetchMatches(firstComp.id);
      } else {
        setCompetitions([]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Gagal memuat data kompetisi');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async (competitionId) => {
    try {
      setLoadingMatches(true);
      const response = await axios.get(`${API}/api/lapangbola/matches`, {
        params: { competition_id: competitionId }
      });
      
      if (response.data && response.data.data) {
        // Filter finished matches only
        const finishedMatches = response.data.data.filter(
          m => m.match_status === 'Finished'
        );
        // Sort by date (newest first)
        finishedMatches.sort((a, b) => {
          const dateA = new Date(a.match_date || a.default_match_date);
          const dateB = new Date(b.match_date || b.default_match_date);
          return dateB - dateA;
        });
        setMatches(finishedMatches);
      } else {
        setMatches([]);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Gagal memuat hasil pertandingan');
      setMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleCompetitionChange = (competitionId) => {
    const comp = competitions.find(c => c.id === Number(competitionId));
    setSelectedCompetition(Number(competitionId));
    setSelectedCompetitionName(comp?.name || '');
    fetchMatches(Number(competitionId));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-ltb-r text-white p-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="mb-2 flex items-center text-white/80 hover:text-white"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali
          </button>
          <h1 className="text-2xl font-bold">Hasil Pertandingan</h1>
          <p className="text-white/80 text-sm mt-1">Hasil pertandingan yang sudah selesai</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Competition Selector */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Kompetisi</label>
          <select
            value={selectedCompetition || ''}
            onChange={(e) => handleCompetitionChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            {competitions.map(comp => (
              <option key={comp.id} value={comp.id}>{comp.name}</option>
            ))}
          </select>
        </div>

        {/* Matches List */}
        {loadingMatches ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
            <p className="text-gray-600">Memuat hasil...</p>
          </div>
        ) : matches.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600 text-lg">Belum ada hasil pertandingan</p>
            <p className="text-gray-400 text-sm mt-2">Hasil akan ditampilkan setelah pertandingan selesai</p>
          </div>
        ) : (
          <div className="space-y-4">
            {selectedCompetitionName && (
              <div className="bg-gradient-ltb-r text-white px-4 py-3 rounded-lg">
                <h2 className="text-lg font-semibold">{selectedCompetitionName}</h2>
              </div>
            )}
            {matches.map((match) => (
              <div key={match.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Match Date Header */}
                <div className="bg-gray-100 px-4 py-2 border-b">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      {formatDate(match.match_date || match.default_match_date)}
                    </span>
                    {match.copa_round_name && (
                      <span className="text-xs bg-ltb-bright/10 text-ltb-bright px-2 py-1 rounded">
                        {match.copa_round_name}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Match Content */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Home Team */}
                    <div className="flex-1 text-center">
                      <img 
                        src={match.home_picture_url} 
                        alt={match.home_club_name}
                        className="w-12 h-12 mx-auto object-contain mb-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>';
                        }}
                      />
                      <p className="text-sm font-medium text-gray-800">{match.home_club_name}</p>
                    </div>
                    
                    {/* Score */}
                    <div className="px-4 text-center">
                      <div className="flex items-center space-x-2">
                        <span className={`text-2xl font-bold ${match.home_score > match.away_score ? 'text-green-600' : match.home_score < match.away_score ? 'text-red-500' : 'text-gray-700'}`}>
                          {match.home_score}
                        </span>
                        <span className="text-gray-400 text-xl">-</span>
                        <span className={`text-2xl font-bold ${match.away_score > match.home_score ? 'text-green-600' : match.away_score < match.home_score ? 'text-red-500' : 'text-gray-700'}`}>
                          {match.away_score}
                        </span>
                      </div>
                      {(match.home_penalty_score > 0 || match.away_penalty_score > 0) && (
                        <p className="text-xs text-gray-500 mt-1">
                          (Pen: {match.home_penalty_score} - {match.away_penalty_score})
                        </p>
                      )}
                    </div>
                    
                    {/* Away Team */}
                    <div className="flex-1 text-center">
                      <img 
                        src={match.away_picture_url} 
                        alt={match.away_club_name}
                        className="w-12 h-12 mx-auto object-contain mb-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>';
                        }}
                      />
                      <p className="text-sm font-medium text-gray-800">{match.away_club_name}</p>
                    </div>
                  </div>
                  
                  {/* Match Status */}
                  <div className="mt-4 text-center">
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      Selesai
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HasilPage;
